import React, { createContext, useContext, useMemo, useState } from 'react';

const STORAGE_KEY = 'adminLanguage';

const translations = {
  en: {
    app: {
      loading: 'Loading...',
      subtitle: 'Admin Panel',
      tagline: 'Advanced FiveM Server Management',
      switchTheme: 'Switch to {theme} mode',
      light: 'light',
      dark: 'dark',
      logout: 'Logout',
      role: {
        root: 'Root Owner',
        admin: 'Administrator',
        moderator: 'Moderator',
        viewer: 'Read-Only'
      },
      tabs: {
        online: 'Online Players',
        lookup: 'Player Lookup',
        economy: 'Economy',
        donate: 'Donate Overview',
        refund: 'Refund System',
        items: 'Item Overview',
        jobs: 'Jobs',
        weapons: 'Weapon Lookup',
        vehicles: 'Vehicle Lookup',
        character: 'Character Tools',
        sites: 'Server Sites',
        users: 'User Management',
        audit: 'Audit Log',
        queries: 'Queries',
        housing: 'Housing'
      },
      changePassword: {
        title: 'Change Password',
        buttonTitle: 'Change Password',
        newPassword: 'New Password',
        newPasswordPlaceholder: 'Min. 8 characters',
        confirmPassword: 'Confirm Password',
        confirmPasswordPlaceholder: 'Repeat new password',
        save: 'Save Password',
        saving: 'Saving…',
        cancel: 'Cancel',
        success: 'Password changed successfully!',
        close: 'Close',
        minLength: 'Password must be at least 8 characters.',
        noMatch: 'Passwords do not match.'
      },
      auth: {
        passkeySignIn: 'Sign in with Passkey',
        passkeyErrors: {
          enterUsername: 'Enter username for passkey login',
          requestFailed: 'Passkey request failed',
          noCredential: 'No credential returned',
          loginFailed: 'Passkey login failed'
        },
        mfaPanel: {
          title: 'Multi-factor verification',
          help: 'Enter the 6-digit code from your authenticator app.',
          label: 'MFA Code',
          cancel: 'Cancel',
          verify: 'Verify',
          verifying: 'Verifying...'
        }
      },
      accountSettings: {
        title: 'Account settings',
        tabs: {
          password: 'Password',
          mfa: 'MFA (TOTP)',
          passkeys: 'Passkeys'
        },
        passkeys: {
          registerNew: 'Register new passkey',
          nameLabel: 'Passkey name',
          namePlaceholder: 'e.g., My MacBook Pro',
          nameHelp: 'Give your passkey a descriptive name (e.g., device name)',
          registered: 'Passkey registered',
          createdLabel: 'Created',
          signCount: 'Sign count',
          nameRequired: 'Passkey name is required',
          removed: 'Passkey removed',
          noPasskeys: 'No passkeys registered.',
          remove: 'Remove',
          cancel: 'Cancel',
          continue: 'Continue'
        },
        mfa: {
          status: 'MFA status',
          enabled: 'Enabled',
          enterCodeToDisable: 'Enter current MFA code to disable',
          setup: 'Setup MFA (TOTP)',
          enable: 'Enable MFA',
          disable: 'Disable MFA',
          setupHelp: 'Scan the QR with an authenticator app and enter the code to enable.'
        }
      },
       webauthn: {
         notEnabled: 'WebAuthn is not enabled on the server.'
       },
      siteManagement: {
        addSite: 'Add Site',
        createSiteTitle: 'Create Site',
        editSiteTitle: 'Edit Site',
        siteId: 'Site ID',
        name: 'Name',
        apiBaseUrl: 'API Base URL',
        apiToken: 'API Token',
        dbBridgeToken: 'DB Bridge Token',
        databaseName: 'Database Name',
        creationCode: 'Site Creation Code',
        creationCodePlaceholder: 'Enter site creation token',
        creationCodes: 'Creation Codes',
        generatedSnippetTitle: 'Generated server.cfg snippet',
        subscriptionPlan: 'Subscription Plan',
        billingStatus: 'Billing Status',
        active: 'Active',
        cancel: 'Cancel',
        create: 'Create',
        update: 'Update',
        existingSites: 'Existing Sites',
        table: {
          siteId: 'Site ID',
          name: 'Name',
          apiBaseUrl: 'API Base URL',
          dbName: 'DB Name',
          plan: 'Plan',
          billing: 'Billing',
          active: 'Active',
          actions: 'Actions',
        },
        edit: 'Edit',
        delete: 'Delete',
        activate: 'Activate',
        deactivate: 'Deactivate',
        yes: 'Yes',
        no: 'No',
        creationCodesTitle: 'Creation Codes',
        newCreationCodePlaceholder: 'secret-creation-code',
        planLabel: 'Plan',
        expiresAt: 'Expires at',
        createCodeButton: 'Create Code',
        codeColumn: 'Code',
        expiresColumn: 'Expires',
        activeColumn: 'Active',
        actionsColumn: 'Actions'
      },
      housing: {
        title: 'Housing Lookup',
        subtitle: 'Search houses and apartments by owner or property name.',
        searchType: 'Search by',
        byOwner: 'Owner Identifier',
        byName: 'House / Apartment Name',
        searchPlaceholder: 'Enter identifier or house name…',
        search: 'Search',
        searching: 'Searching…',
        noResults: 'No properties found matching your search.',
        failed: 'Failed to search housing',
        resultsCount: '{count} propert{count, plural, one{y} other{ies}} found',
        houseName: 'Property',
        owner: 'Owner',
        price: 'Price',
        stashName: 'Stash ID',
        lastPayment: 'Last Payment',
        renting: 'Renting',
        shared: 'Shared',
        features: 'Features',
        pool: 'Pool',
        garden: 'Garden',
        camera: 'Camera',
        canVisit: 'Visitable',
        stars: 'Stars',
        viewStash: 'View Stash',
        hideStash: 'Hide Stash',
        stashLoading: 'Loading stash…',
        stashFailed: 'Failed to load stash',
        stashEmpty: 'Stash is empty.',
        stashTitle: 'Stash Contents',
        stashItem: 'Item',
        stashCount: 'Count',
        unknownPlayer: 'Unknown Player',
        noDescription: 'No description',
        yes: 'Yes',
        no: 'No'
      }
    },
    refund: {
      title: 'Refund System',
      subtitle: 'Give items to players as refunds',
      step1: 'Step 1: Select Player',
      step2: 'Step 2: Add Items',
      step3: 'Step 3: Registration',
      step4: 'Step 4: Confirm Refund',
      searchPlaceholder: 'Search by name or ID...',
      itemSearchPlaceholder: 'Search items...',
      changePlayer: 'Change Player',
      refreshItems: 'Refresh items from server',
      noItemsFound: 'No items found',
      typeToSearch: 'Type to search items',
      noItemsAdded: 'No items added yet',
      itemCount: '{count} item(s)',
      giveRefund: 'Give Refund',
      givingRefund: 'Giving Refund...',
      selectPlayer: 'Please select a player',
      addItems: 'Please add items to refund',
      itemAlreadyAdded: 'Item already added',
      errorLoadingItems: 'Error loading items: {error}',
      errorSearchingPlayers: 'Error searching players: {error}',
      errorGivingRefund: 'Error giving refund: {error}',
      noWeaponPermission: 'You do not have permission to refund weapon items',
      refundSuccess: 'Refund given to {player}!',
      refreshSuccess: 'Item cache refreshed ({fetched} items).',
      resultsTitle: 'Refund results ({count})',
      itemGiven: 'Item given',
      itemFailed: 'Item failed',
      queueFailed: 'Queue failed items',
      queuedFailedItems: 'Queued {count} failed item(s)',
      errorQueueingFailed: 'Error queueing failed items: {error}',
      weaponCount: '{count} weapon item(s)',
      registrationNone: 'No weapon items need registration yet',
      registerWeapons: 'Register weapon serials',
      registeringWeapons: 'Registering weapons...',
      registrationSuccess: 'Registered {count} weapon item(s)',
      errorRegisteringWeapons: 'Error registering weapon items: {error}',
      weaponNeedsRegistration: 'Weapon items must be registered before refunding',
      weaponRegistered: 'Registered',
      weaponPendingRegistration: 'Pending registration',
      weaponRefundLocked: 'Weapon refunds require extra permission',
      weaponRefundLockedShort: 'Locked',
      weaponRefundAllowed: 'You can refund weapon items',
      weaponRefundAllowedShort: 'Weapon',
      noPermission: "You don't have permission to access this feature",
      failedToLoadItems: 'Failed to load items',
      failedToSearchPlayers: 'Failed to search players'
    },
    refundQueue: {
      title: 'Refund Queue',
      subtitle: 'Review and process queued refund items.',
      totalItems: 'Showing {count} item(s)',
      retry: 'Retry',
      delete: 'Delete',
      pending: 'Pending',
      failed: 'Failed',
      done: 'Done',
      all: 'All',
      searchPlaceholder: 'Search by player or item...',
      refresh: 'Refresh',
      selectAll: 'Select all visible',
      unselectAll: 'Unselect all visible',
      retrySelected: 'Retry selected',
      deleteSelected: 'Delete selected',
      player: 'Player',
      item: 'Item',
      quantity: 'Qty',
      status: 'Status',
      attempts: 'Attempts',
      delivery: 'Delivery',
      lastError: 'Last error',
      created: 'Created',
      loading: 'Loading queue...',
      empty: 'No queued items found.',
      retrySummary: 'Retry complete. Success: {ok}, Failed: {failed}',
      deleteSummary: 'Deleted {count} queued item(s)',
      deleteConfirm: 'Delete {count} queued item(s)?'
    },
    
    users: {
      title: 'User Management',
      youAreHere: 'This is your current rank',
      subtitle: 'Manage accounts, roles, and scoped permissions.',
      createUser: 'Create User',
      accessDeniedTitle: 'Access denied',
      accessDeniedDesc: 'You do not have permission to view user management.',
      loadingUsers: 'Loading users...',
      error: 'Error',
      totalAccounts: 'Total accounts',
      enabled: 'Enabled',
      disabled: 'Disabled',
      idle: 'Idle',
      table: {
        user: 'User',
        role: 'Role',
        discord: 'Discord',
        scope: 'Permission scope',
        status: 'Status',
        created: 'Created',
        actions: 'Actions'
      },
      discordLinked: 'Linked',
      discordUnlinked: 'Not linked',
      noPermissions: 'No permissions',
      more: '+{count} more',
      edit: 'Edit',
      delete: 'Delete',
      editTitle: 'Edit user',
      deleteTitle: 'Delete user',
      modal: {
        editUser: 'Edit User',
        createUser: 'Create New User',
        username: 'Username',
        password: 'Password',
        keepPassword: '(leave empty to keep existing)',
        displayName: 'Display Name',
        email: 'Email',
        discordId: 'Discord User ID',
        discordPlaceholder: 'Optional: numeric Discord account id',
        role: 'Role',
        permissionPreset: 'Permission Preset',
        applyRoleDefault: 'Apply role default',
        customSelection: 'Custom selection',
        permissions: 'Permissions ({count} selected)',
        selectedCount: '{selected} of {total} selected',
        selectAll: 'Select all',
        clear: 'Clear',
        cancel: 'Cancel',
        saveChanges: 'Save Changes'
      },
      fieldHintLogin: 'Used for login.',
      readOnlyMode: 'Read-only mode. You need jobs.edit to modify jobs.',
      languageLabel: 'Language',
      languageEnglish: 'English',
      languageDanish: 'Danish',
      deleteConfirm: 'Delete this user permanently? This cannot be undone.',
      failedPermissionMetadata: 'Failed to fetch permission metadata',
      failedUsers: 'Failed to fetch users',
      failedCreate: 'Failed to create user',
      failedUpdate: 'Failed to update user',
      failedDelete: 'Failed to delete user',
      failedStatus: 'Failed to update user status'
    },
    permissions: {
      playersView: 'View players',
      playersEdit: 'Edit player data',
      playersDelete: 'Delete player data',
      phonesRename: 'Rename phone number',
      phonesPrivateMessagesView: 'View private messages',
      economyView: 'View economy',
      economyEdit: 'Edit economy balances',
      itemsView: 'View items',
      itemsEdit: 'Edit inventories',
      refundView: 'View refund system',
      refundEdit: 'Manage refunds (give items)',
      refundRefresh: 'Refresh refund item cache',
      jobsView: 'View jobs',
      jobsEdit: 'Edit jobs',
      jobsCatalogView: 'View jobs catalog',
      jobsCatalogEdit: 'Edit jobs catalog',
      vehiclesView: 'View vehicles',
      vehiclesEdit: 'Edit vehicles',
      donateView: 'View donations',
      codesGenerate: 'Generate donation codes',
      codesManage: 'Manage donation codes',
      coinsManage: 'Manage coins',
      charactersView: 'View characters',
      charactersEdit: 'Edit characters',
      charactersMove: 'Move characters',
      charactersDelete: 'Delete characters',
      usersView: 'View users',
      usersCreate: 'Create users',
      usersEdit: 'Edit users',
      usersDelete: 'Delete users',
      auditView: 'View audit logs'
    },
    roles: {
      rootLabel: 'Root Owner',
      rootDesc: 'Full unrestricted access.',
      adminLabel: 'Administrator',
      adminDesc: 'Manages most systems and users.',
      moderatorLabel: 'Moderator',
      moderatorDesc: 'Supports operations and player actions.',
      viewerLabel: 'Read-Only',
      viewerDesc: 'Can view data without making changes.',
      ownerLabel: 'Site Owner',
      ownerDesc: 'Owns this site. Can manage all users and settings.'
    },
    auditLog: {
      title: 'Audit Log',
      subtitle: 'Complete history of all admin actions',
      refresh: 'Refresh',
      loading: 'Loading…',
      loadingLogs: 'Loading audit logs…',
      failed: 'Failed to load audit logs',
      empty: 'No audit log entries found.',
      totalEntries: '{total} total entries',
      page: 'Page {page} of {total}',
      prev: '‹ Prev',
      next: 'Next ›',
      rawDetails: 'Raw details:',
      table: {
        time: 'Date / Time',
        admin: 'Admin User',
        action: 'Action',
        details: 'Details',
        ip: 'IP Address'
      },
      categories: {
        all: 'All',
        auth: 'Auth',
        users: 'Users',
        economy: 'Economy',
        donate: 'Donate',
        items: 'Items',
        jobs: 'Jobs',
        vehicles: 'Vehicles',
        characters: 'Characters',
        phones: 'Phones',
        companies: 'Company Finance',
        heists: 'Heists',
        refund: 'Refund'
      },
      actions: {
        LOGIN: 'Login',
        LOGIN_PASSKEY: 'Passkey Login',
        LOGIN_DISCORD: 'Discord Login',
        LOGOUT: 'Logout',
        PASSKEY_REMOVED: 'Passkey Removed',
        PASSWORD_CHANGED: 'Password Changed',
        USER_CREATED: 'User Created',
        USER_UPDATED: 'User Updated',
        USER_DELETED: 'User Deleted',
        MONEY_GIVEN: 'Money Given',
        MONEY_TAKEN: 'Money Taken',
        DONATE_CODE_GENERATED: 'Code Generated',
        DONATE_CODE_DELETED: 'Code Deleted',
        ITEM_UPDATED: 'Item Updated',
        JOB_CREATED: 'Job Created',
        JOB_UPDATED: 'Job Updated',
        JOB_REMOVED: 'Job Removed',
        JOB_DELETED: 'Job Deleted',
        JOB_SET_PRIMARY: 'Primary Job Set',
        JOB_GRADE_UPDATED: 'Grade Updated',
        JOB_GRADE_DELETED: 'Grade Deleted',
        VEHICLE_DELETED: 'Vehicle Deleted',
        CHARACTER_MOVED: 'Character Moved',
        CHARACTER_PED_SET: 'Ped Model Set',
        CHARACTER_DELETED: 'Character Deleted',
        PHONE_RENAMED: 'Phone Number Changed',
        COMPANY_DEPOSIT: 'Company Deposit',
        COMPANY_WITHDRAW: 'Company Withdraw',
        COMPANY_SCHEDULE_CREATED: 'Schedule Created',
        COMPANY_SCHEDULE_UPDATED: 'Schedule Updated',
        COMPANY_SCHEDULE_DELETED: 'Schedule Deleted',
        HEIST_GIVE_XP: 'Heist XP Given',
        HEIST_GIVE_CREDITS: 'Heist Credits Given',
        HEIST_REMOVE_CREDITS: 'Heist Credits Removed',
        REFUND_ITEMS_GIVEN: 'Refund Items Given',
        REFUND_CACHE_REFRESH: 'Refund Cache Refreshed',
        refund_items: 'Refund Items Given',
        refund: {
          cache: {
            refresh: 'Refund Cache Refreshed'
          }
        }
      }
    },
    login: {
      selectServerLabel: 'Select a server',
      selectServerHint: 'Choose a server to log in to',
      adminAccess: 'Admin Access',
      username: 'Username',
      usernamePlaceholder: 'Enter your username',
      password: 'Password',
      passwordPlaceholder: 'Enter your password',
      signIn: 'Sign In',
      authenticating: 'Authenticating...',
      continueDiscord: 'Continue with Discord',
      discordConnecting: 'Connecting...'
    }
  },
  da: {
    app: {
      loading: 'Indlæser...',
      subtitle: 'Adminpanel',
      tagline: 'Avanceret FiveM serverstyring',
      switchTheme: 'Skift til {theme} tilstand',
      light: 'lys',
      dark: 'mørk',
      logout: 'Log ud',
      role: {
        root: 'Root ejer',
        admin: 'Administrator',
        moderator: 'Moderator',
        viewer: 'Kun læsning'
      },
      tabs: {
        online: 'Online spillere',
        lookup: 'Spiller opslag',
        economy: 'økonomi',
        donate: 'Donationsoverblik',
        refund: 'Refund System',
        items: 'Item overblik',
        jobs: 'Jobs',
        weapons: 'Våben opslag',
        vehicles: 'køretøj opslag',
        character: 'Karakterværktøj',
        sites: 'Servere',
        users: 'Brugeradministration',
        audit: 'Revisionslog',
        queries: 'Queries',
        housing: 'Boliger'
      },
      changePassword: {
        title: 'Skift adgangskode',
        buttonTitle: 'Skift adgangskode',
        newPassword: 'Ny adgangskode',
        newPasswordPlaceholder: 'Min. 8 tegn',
        confirmPassword: 'Bekræft adgangskode',
        confirmPasswordPlaceholder: 'Gentag ny adgangskode',
        save: 'Gem adgangskode',
        saving: 'Gemmer…',
        cancel: 'Annuller',
        success: 'Adgangskode ændret!',
        close: 'Luk',
        minLength: 'Adgangskoden skal være mindst 8 tegn.',
        noMatch: 'Adgangskoderne stemmer ikke overens.'
      },
      accountSettings: {
        title: 'Kontoindstillinger',
        tabs: {
          password: 'Adgangskode',
          mfa: 'MFA (TOTP)',
          passkeys: 'Passkeys'
        },
        passkeys: {
          registerNew: 'Registrer ny passkey',
          nameLabel: 'Passkey navn',
          namePlaceholder: 'fx My MacBook Pro',
          nameHelp: 'Giv din passkey et beskrivende navn (fx enhedsnavn)',
          registered: 'Passkey registreret',
           createdLabel: 'Oprettet',
           signCount: 'Sign tæller',
           nameRequired: 'Passkey navn kræves',
           removed: 'Passkey fjernet',
          noPasskeys: 'Ingen passkeys registreret.',
          remove: 'Fjern',
          cancel: 'Annuller',
          continue: 'Fortsæt'
        },
        mfa: {
          status: 'MFA status',
          enabled: 'Aktiveret',
          enterCodeToDisable: 'Indtast nuværende MFA-kode for at deaktivere',
          setup: 'Opsæt MFA (TOTP)',
          enable: 'Aktiver MFA',
          disable: 'Deaktiver MFA',
          setupHelp: 'Scan QR-koden med en autentificeringsapp og indtast koden for at aktivere.'
        }
      },
      webauthn: {
        notEnabled: 'WebAuthn er ikke aktiveret på serveren.'
      },
      siteManagement: {
        addSite: 'Tilføj site',
        createSiteTitle: 'Opret site',
        editSiteTitle: 'Rediger site',
        siteId: 'Site ID',
        name: 'Navn',
        apiBaseUrl: 'API base URL',
        apiToken: 'API token',
        dbBridgeToken: 'DB bridge token',
        databaseName: 'Databasenavn',
        creationCode: 'Site oprettelseskode',
        creationCodePlaceholder: 'Indtast site oprettelsestoken',
        creationCodes: 'Oprettelseskoder',
        generatedSnippetTitle: 'Genereret server.cfg snippet',
        subscriptionPlan: 'Abonnement',
        billingStatus: 'Fakturering',
        active: 'Aktiv',
        cancel: 'Annuller',
        create: 'Opret',
        update: 'Opdater',
        existingSites: 'Eksisterende sites',
        table: {
          siteId: 'Site ID',
          name: 'Navn',
          apiBaseUrl: 'API base URL',
          dbName: 'DB navn',
          plan: 'Plan',
          billing: 'Fakturering',
          active: 'Aktiv',
          actions: 'Handlinger',
        },
        edit: 'Rediger',
        delete: 'Slet',
        activate: 'Aktiver',
        deactivate: 'Deaktiver',
        yes: 'Ja',
        no: 'Nej',
        creationCodesTitle: 'Oprettelseskoder',
        newCreationCodePlaceholder: 'secret-creation-code',
        planLabel: 'Plan',
        expiresAt: 'Udløber',
        createCodeButton: 'Opret kode',
        codeColumn: 'Kode',
        expiresColumn: 'Udløber',
        activeColumn: 'Aktiv',
        actionsColumn: 'Handlinger'
      },
      housing: {
        title: 'Boligopslag',
        subtitle: 'Søg huse og lejligheder via ejer eller ejendommens navn.',
        searchType: 'Søg efter',
        byOwner: 'Ejer-identifikator',
        byName: 'Hus / Lejlighedsnavn',
        searchPlaceholder: 'Indtast identifikator eller navn…',
        search: 'Søg',
        searching: 'Søger…',
        noResults: 'Ingen ejendomme fundet.',
        failed: 'Kunne ikke søge boliger',
        resultsCount: '{count} ejendom(me) fundet',
        houseName: 'Ejendom',
        owner: 'Ejer',
        price: 'Pris',
        stashName: 'Stash-ID',
        lastPayment: 'Seneste betaling',
        renting: 'Lejer',
        shared: 'Delt',
        features: 'Funktioner',
        pool: 'Pool',
        garden: 'Have',
        camera: 'Kamera',
        canVisit: 'Kan besøges',
        stars: 'Stjerner',
        viewStash: 'Se stash',
        hideStash: 'Skjul stash',
        stashLoading: 'Henter stash…',
        stashFailed: 'Kunne ikke hente stash',
        stashEmpty: 'Stash er tom.',
        stashTitle: 'Stash-indhold',
        stashItem: 'Item',
        stashCount: 'Antal',
        unknownPlayer: 'Ukendt spiller',
        noDescription: 'Ingen beskrivelse',
        yes: 'Ja',
        no: 'Nej'
      }
    },
    refund: {
      title: 'Refund System',
      subtitle: 'Giv items til spillere som refund',
      step1: 'Trin 1: Vælg spiller',
      step2: 'Trin 2: Tilføj items',
      step3: 'Trin 3: Registrering',
      step4: 'Trin 4: Bekræft refund',
      searchPlaceholder: 'Søg efter navn eller ID...',
      itemSearchPlaceholder: 'Søg items...',
      changePlayer: 'Skift spiller',
      refreshItems: 'Opdater items fra server',
      noItemsFound: 'Ingen items fundet',
      typeToSearch: 'Skriv for at søge items',
      noItemsAdded: 'Ingen items tilføjet endnu',
      itemCount: '{count} item(s)',
      giveRefund: 'Giv refund',
      givingRefund: 'Giver refund...',
      selectPlayer: 'Vælg venligst en spiller',
      addItems: 'Tilføj venligst items til refund',
      itemAlreadyAdded: 'Item allerede tilføjet',
      errorLoadingItems: 'Fejl ved indlæsning af items: {error}',
      errorSearchingPlayers: 'Fejl ved søgning af spillere: {error}',
      errorGivingRefund: 'Fejl ved giving af refund: {error}',
      noWeaponPermission: 'Du har ikke tilladelse til at refundere våben',
      refundSuccess: 'Refund givet til {player}!',
      resultsTitle: 'Refund resultater ({count})',
      itemGiven: 'Item givet',
      itemFailed: 'Item fejlede',
      queueFailed: 'Kø failed items',
      queuedFailedItems: 'Køede {count} failed item(s)',
      errorQueueingFailed: 'Fejl ved kø af failed items: {error}',
      weaponCount: '{count} våben item(s)',
      registrationNone: 'Ingen våben kræver registrering endnu',
      registerWeapons: 'Registrer våbens serienumre',
      registeringWeapons: 'Registrerer våben...',
      registrationSuccess: 'Registrerede {count} våben',
      errorRegisteringWeapons: 'Fejl ved registrering af våben: {error}',
      weaponNeedsRegistration: 'Våben skal registreres før refund',
      weaponRegistered: 'Registreret',
      weaponPendingRegistration: 'Afventer registrering',
      weaponRefundLocked: 'Refund af våben kræver ekstra tilladelse',
      weaponRefundLockedShort: 'Låst',
      weaponRefundAllowed: 'Du kan refundere våben',
      weaponRefundAllowedShort: 'Våben',
      noPermission: 'Du har ikke tilladelse til at få adgang til denne funktion',
      failedToLoadItems: 'Kunne ikke indlæse items',
      failedToSearchPlayers: 'Kunne ikke søge efter spillere'
    },
    refundQueue: {
      title: 'Refund Queue',
      subtitle: 'Se og behandl køede refund items.',
      totalItems: 'Viser {count} item(s)',
      retry: 'Prøv igen',
      delete: 'Slet',
      pending: 'Afventer',
      failed: 'Fejlet',
      done: 'Færdig',
      all: 'Alle',
      searchPlaceholder: 'Søg efter spiller eller item...',
      refresh: 'Opdater',
      selectAll: 'Vælg alle synlige',
      unselectAll: 'Fjern valg af synlige',
      retrySelected: 'Prøv valgte igen',
      deleteSelected: 'Slet valgte',
      player: 'Spiller',
      item: 'Item',
      quantity: 'Antal',
      status: 'Status',
      attempts: 'Forsøg',
      delivery: 'Levering',
      lastError: 'Sidste fejl',
      created: 'Oprettet',
      loading: 'Indlæser kø...',
      empty: 'Ingen køede items fundet.',
      retrySummary: 'Retry færdig. Succes: {ok}, Fejlet: {failed}',
      deleteSummary: 'Slettede {count} køede item(s)',
      deleteConfirm: 'Slet {count} køede item(s)?'
    },
    users: {
      title: 'Brugeradministration',
      youAreHere: 'Dette er din nuværende rang',
      subtitle: 'Administrer konti, roller og afgraensede tilladelser.',
      createUser: 'Opret bruger',
      accessDeniedTitle: 'Adgang afvist',
      accessDeniedDesc: 'Du har ikke tilladelse til at se brugeradministration.',
      loadingUsers: 'Indlæser brugere...',
      error: 'Fejl',
      totalAccounts: 'Samlet antal konti',
      enabled: 'Aktiveret',
      disabled: 'Deaktiveret',
      idle: 'Inaktiv',
      table: {
        user: 'Bruger',
        role: 'Rolle',
        discord: 'Discord',
        scope: 'Tilladelsesniveau',
        status: 'Status',
        created: 'Oprettet',
        actions: 'Handlinger'
      },
      discordLinked: 'Tilknyttet',
      discordUnlinked: 'Ikke tilknyttet',
      noPermissions: 'Ingen tilladelser',
      more: '+{count} flere',
      edit: 'Rediger',
      delete: 'Slet',
      editTitle: 'Rediger bruger',
      deleteTitle: 'Slet bruger',
      modal: {
        editUser: 'Rediger bruger',
        createUser: 'Opret ny bruger',
        username: 'Brugernavn',
        password: 'Adgangskode',
        keepPassword: '(lad vaere tom for at beholde eksisterende)',
        displayName: 'Visningsnavn',
        email: 'E-mail',
        discordId: 'Discord bruger-id',
        discordPlaceholder: 'Valgfrit: numerisk Discord kontoid',
        role: 'Rolle',
        permissionPreset: 'Tilladelses preset',
        applyRoleDefault: 'Anvend standard for rolle',
        customSelection: 'Tilpasset valg',
        permissions: 'Tilladelser ({count} valgt)',
        selectedCount: '{selected} af {total} valgt',
        selectAll: 'vælg alle',
        clear: 'Ryd',
        cancel: 'Annuller',
        saveChanges: 'Gem ændringer'
      },
      fieldHintLogin: 'Bruges til login.',
      readOnlyMode: 'Kun læsning. Du skal have jobs.edit for at ændre jobs.',
      languageLabel: 'Sprog',
      languageEnglish: 'Engelsk',
      languageDanish: 'Dansk',
      deleteConfirm: 'Slet denne bruger permanent? Dette kan ikke fortrydes.',
      failedPermissionMetadata: 'Kunne ikke hente tilladelsesmetadata',
      failedUsers: 'Kunne ikke hente brugere',
      failedCreate: 'Kunne ikke oprette bruger',
      failedUpdate: 'Kunne ikke opdatere bruger',
      failedDelete: 'Kunne ikke slette bruger',
      failedStatus: 'Kunne ikke opdatere brugerstatus'
    },
    permissions: {
      playersView: 'Se spillere',
      playersEdit: 'Rediger spillerdata',
      playersDelete: 'Slet spillerdata',
      phonesRename: 'Omdøb telefonnummer',
      phonesPrivateMessagesView: 'Se private beskeder',
      economyView: 'Se økonomi',
      economyEdit: 'Rediger økonomiske saldi',
      itemsView: 'Se items',
      itemsEdit: 'Rediger inventories',
      jobsView: 'Se jobs',
      jobsEdit: 'Rediger jobs',
      jobsCatalogView: 'Se jobs katalog',
      jobsCatalogEdit: 'Rediger jobs katalog',
      vehiclesView: 'Se køretøjer',
      vehiclesEdit: 'Rediger køretøjer',
      donateView: 'Se donationer',
      codesGenerate: 'Generer donationskoder',
      codesManage: 'Administrer donationskoder',
      coinsManage: 'Administrer coins',
      charactersView: 'Se karakterer',
      charactersEdit: 'Rediger karakterer',
      charactersMove: 'Flyt karakterer',
      charactersDelete: 'Slet karakterer',
      usersView: 'Se brugere',
      usersCreate: 'Opret brugere',
      usersEdit: 'Rediger brugere',
      usersDelete: 'Slet brugere',
      auditView: 'Se revisionslog'
    },
    roles: {
      rootLabel: 'Root ejer',
      rootDesc: 'Fuld og ubegranset adgang.',
      adminLabel: 'Administrator',
      adminDesc: 'Administrerer de fleste systemer og brugere.',
      moderatorLabel: 'Moderator',
      moderatorDesc: 'Stoetter drift og spillerhandlinger.',
      viewerLabel: 'Kun læsning',
      viewerDesc: 'Kan se data uden at ændre noget.',
      ownerLabel: 'Site ejer',
      ownerDesc: 'Ejer dette site. Kan administrere alle brugere og indstillinger.'
    },
    auditLog: {
      title: 'Revisionslog',
      subtitle: 'Komplet oversigt over alle admin-handlinger',
      refresh: 'Opdater',
      loading: 'Indlæser…',
      loadingLogs: 'Indlæser revisionslog…',
      failed: 'Kunne ikke hente revisionslog',
      empty: 'Ingen log-poster fundet.',
      totalEntries: '{total} poster i alt',
      page: 'Side {page} af {total}',
      prev: '‹ Forrige',
      next: 'Næste ›',
      rawDetails: 'Rådata:',
      table: {
        time: 'Dato / Tid',
        admin: 'Admin-bruger',
        action: 'Handling',
        details: 'Detaljer',
        ip: 'IP-adresse'
      },
      categories: {
        all: 'Alle',
        auth: 'Auth',
        users: 'Brugere',
        economy: 'Økonomi',
        donate: 'Donationer',
        items: 'Items',
        jobs: 'Jobs',
        vehicles: 'Køretøjer',
        characters: 'Karakterer',
        phones: 'Telefoner',
        companies: 'Virksomhedsøkonomi',
        heists: 'Heists',
        refund: 'Refund'
      },
      actions: {
        LOGIN: 'Login',
        LOGIN_PASSKEY: 'Passkey Login',
        LOGIN_DISCORD: 'Discord Login',
        LOGOUT: 'Logout',
        PASSKEY_REMOVED: 'Passkey fjernet',
        PASSWORD_CHANGED: 'Adgangskode ændret',
        USER_CREATED: 'Bruger oprettet',
        USER_UPDATED: 'Bruger opdateret',
        USER_DELETED: 'Bruger slettet',
        MONEY_GIVEN: 'Penge givet',
        MONEY_TAKEN: 'Penge taget',
        DONATE_CODE_GENERATED: 'Kode genereret',
        DONATE_CODE_DELETED: 'Kode slettet',
        ITEM_UPDATED: 'Item opdateret',
        JOB_CREATED: 'Job oprettet',
        JOB_UPDATED: 'Job opdateret',
        JOB_REMOVED: 'Job fjernet',
        JOB_DELETED: 'Job slettet',
        JOB_SET_PRIMARY: 'Primært job sat',
        JOB_GRADE_UPDATED: 'Grad opdateret',
        JOB_GRADE_DELETED: 'Grad slettet',
        VEHICLE_DELETED: 'Køretøj slettet',
        CHARACTER_MOVED: 'Karakter flyttet',
        CHARACTER_PED_SET: 'Ped model sat',
        CHARACTER_DELETED: 'Karakter slettet',
        PHONE_RENAMED: 'Telefonnummer ændret',
        COMPANY_DEPOSIT: 'Virksomhedsindsætning',
        COMPANY_WITHDRAW: 'Virksomhedsudtagning',
        COMPANY_SCHEDULE_CREATED: 'Planlægning oprettet',
        COMPANY_SCHEDULE_UPDATED: 'Planlægning opdateret',
        COMPANY_SCHEDULE_DELETED: 'Planlægning slettet',
        HEIST_GIVE_XP: 'Heist XP givet',
        HEIST_GIVE_CREDITS: 'Heist-kroner givet',
        HEIST_REMOVE_CREDITS: 'Heist-kroner fjernet',
        REFUND_ITEMS_GIVEN: 'Refund items givet',
        REFUND_CACHE_REFRESH: 'Refund cache opdateret',
        refund_items: 'Refund items givet',
        refund: {
          cache: {
            refresh: 'Refund cache opdateret'
          }
        }
      }
    },
    login: {
      selectServerLabel: 'Vælg en server',
      selectServerHint: 'Vælg en server at logge ind på',
      adminAccess: 'Admin adgang',
      username: 'Brugernavn',
      usernamePlaceholder: 'Indtast dit brugernavn',
      password: 'Adgangskode',
      passwordPlaceholder: 'Indtast din adgangskode',
      signIn: 'Log ind',
      authenticating: 'Autentificerer...',
      continueDiscord: 'Fortsæt med Discord',
      discordConnecting: 'Forbinder...'
    }
  }
};

const I18nContext = createContext(null);

const getByPath = (obj, path) => {
  return path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
};

const formatTemplate = (value, params = {}) => {
  if (typeof value !== 'string') {
    return value;
  }

  return value.replace(/\{(\w+)\}/g, (_, token) => {
    return params[token] !== undefined ? String(params[token]) : `{${token}}`;
  });
};

export const I18nProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'da' ? 'da' : 'en';
  });

  const setLanguage = (nextLanguage) => {
    const normalized = nextLanguage === 'da' ? 'da' : 'en';
    localStorage.setItem(STORAGE_KEY, normalized);
    setLanguageState(normalized);
  };

  const value = useMemo(() => {
    const t = (key, params = {}) => {
      const active = getByPath(translations[language], key);
      const fallback = getByPath(translations.en, key);
      const resolved = active !== undefined ? active : fallback !== undefined ? fallback : key;
      return formatTemplate(resolved, params);
    };

    return {
      language,
      setLanguage,
      t
    };
  }, [language]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
};

export default I18nContext;
